/**
 * Unilar - Navegação PDP
 * Shows items only when their target section exists on the page.
 */

class UnilarPdpNav extends HTMLElement {
  /** @type {HTMLAnchorElement[]} */
  #links = [];

  /** @type {HTMLElement | null} */
  #nav = null;

  /** @type {HTMLDivElement | null} */
  #placeholder = null;

  /** @type {boolean} */
  #isSticky = false;

  /** @type {number | null} */
  #stickyRaf = null;

  /** @type {boolean} */
  #hasClickListeners = false;

  connectedCallback() {
    if (!this.#syncVisibleItems()) return;

    if (!this.#hasClickListeners) {
      this.#links.forEach((link) => {
        link.addEventListener('click', this.#handleClick);
      });

      this.#hasClickListeners = true;
    }

    this.#nav = this.closest('.pdp-nav');
    this.#setupSticky();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);

    if (this.#stickyRaf !== null) {
      cancelAnimationFrame(this.#stickyRaf);
      this.#stickyRaf = null;
    }
  }

  /**
   * @returns {boolean}
   */
  #syncVisibleItems() {
    const nav = this.closest('.pdp-nav');
    const items = [...this.querySelectorAll('.pdp-nav__item')];

    items.forEach((item) => {
      const link = item.querySelector('[data-pdp-nav-target]');
      const target = link ? this.#getTargetElement(link) : null;

      if (!target) {
        item.remove();
      }
    });

    this.#links = [...this.querySelectorAll('.pdp-nav__link[data-pdp-nav-target]')];

    if (!this.#links.length) {
      nav?.closest('.shopify-section')?.remove();
      return false;
    }

    nav?.removeAttribute('hidden');
    return true;
  }

  /**
   * @param {Element} link
   * @returns {HTMLElement | null}
   */
  #getTargetElement(link) {
    const targetId = link.getAttribute('data-pdp-nav-target');
    return targetId ? document.getElementById(targetId) : null;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const target = this.#getTargetElement(link);
    if (!target) return;

    event.preventDefault();

    const offset = this.#getAnchorScrollOffset();
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    const targetId = link.getAttribute('data-pdp-nav-target');

    window.scrollTo({ top, behavior: 'smooth' });

    if (targetId) {
      history.replaceState(null, '', `#${targetId}`);
    }
  };

  /**
   * @returns {number}
   */
  #getHeaderOffset() {
    const styles = getComputedStyle(document.documentElement);
    const cssHeight = parseInt(styles.getPropertyValue('--header-height'), 10);
    if (cssHeight) return cssHeight;

    const header = document.querySelector('header-component, .header, header, .unilar-header');
    return header instanceof HTMLElement ? header.offsetHeight : 80;
  }

  /**
   * @returns {number}
   */
  #getAnchorScrollOffset() {
    return this.#getHeaderOffset() + (this.#isMobileStickyViewport() ? this.#getNavBarHeight() : 0);
  }

  #setupSticky() {
    if (!this.#nav) return;

    if (!this.#placeholder) {
      const currentParent = this.#nav.parentElement;
      if (currentParent?.classList.contains('pdp-nav__placeholder')) {
        this.#placeholder = currentParent;
      }
    }

    if (this.#placeholder) {
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
      window.addEventListener('resize', this.#handleResize, { passive: true });
      this.#updateScrollOffsetVar();
      this.#updateStickyState();
      return;
    }

    const parent = this.#nav.parentNode;
    if (!parent) return;

    this.#placeholder = document.createElement('div');
    this.#placeholder.className = 'pdp-nav__placeholder';
    parent.insertBefore(this.#placeholder, this.#nav);
    this.#placeholder.appendChild(this.#nav);

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });
    this.#updateScrollOffsetVar();
    this.#updateStickyState();
  }

  #handleScroll = () => {
    if (this.#stickyRaf !== null) return;

    this.#stickyRaf = requestAnimationFrame(() => {
      this.#stickyRaf = null;
      this.#updateStickyState();
    });
  };

  #handleResize = () => {
    this.#updateStickyState();
    this.#updateScrollOffsetVar();
  };

  #updateStickyState() {
    if (!this.#nav || !this.#placeholder) return;

    if (!this.#isMobileStickyViewport()) {
      this.#resetStickyState();
      this.#updateScrollOffsetVar();
      return;
    }

    const naturalTop = this.#placeholder.getBoundingClientRect().top + window.scrollY;
    const shouldStick = window.scrollY >= naturalTop;

    if (shouldStick && !this.#isSticky) {
      const height = this.#nav.offsetHeight;
      this.#nav.classList.add('is-sticky');
      this.#placeholder.style.minHeight = `${height}px`;
      this.#placeholder.classList.add('is-sticky');
      this.#isSticky = true;
    } else if (!shouldStick && this.#isSticky) {
      this.#nav.classList.remove('is-sticky');
      this.#placeholder.classList.remove('is-sticky');
      this.#placeholder.style.minHeight = '';
      this.#isSticky = false;
    } else if (this.#isSticky) {
      this.#placeholder.style.minHeight = `${this.#nav.offsetHeight}px`;
    }

    this.#updateScrollOffsetVar();
  }

  #resetStickyState() {
    if (!this.#nav || !this.#placeholder) return;

    this.#nav.classList.remove('is-sticky');
    this.#placeholder.classList.remove('is-sticky');
    this.#placeholder.style.minHeight = '';
    this.#isSticky = false;
  }

  /**
   * @returns {boolean}
   */
  #isMobileStickyViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * @returns {number}
   */
  #getNavBarHeight() {
    return this.#nav?.offsetHeight ?? 0;
  }

  #updateScrollOffsetVar() {
    const offset = this.#getAnchorScrollOffset();
    document.documentElement.style.setProperty('--pdp-nav-scroll-offset', `${offset}px`);
  }
}

if (!customElements.get('unilar-pdp-nav')) {
  customElements.define('unilar-pdp-nav', UnilarPdpNav);
}
